import {
  Box,
  Flex,
  Image,
  Spinner,
  VStack,
  IconButton,
  Button,
  Center,
  Text,
  Alert
} from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';

import { useUserStore } from '@store/app.store';
import { useLoginShared, useSetDocumentTitle } from '@util/hooks';
import { EyeIcon, EyeOffIcon } from '@assets/icons';
import { ForaLogoWhite } from '@assets/images';
import FormInput from '@components/general/form/form-input.component';
import TermsAndConditions from '@components/login/terms-conditions.component';
import OAuthStack from './oauth-stack.component';

import { getEnvironmentName } from '@util/helpers';
import { generateOtp } from '@services/query-services/people.query-service';

function LoginPage({ standardLoginEnabled = true, oktaEnabled = true, passwordEnabled = true }) {
  useSetDocumentTitle('Login - FORA');
  const location = useLocation();
  const [query] = useSearchParams();
  const params = { inviteFor: query.get('inviteFor'), paymentId: query.get('paymentIntentId') };
  const { inviteFor, paymentId } = params;
  const {
    handleSubmit,
    register,
    getValues,
    formState: { dirtyFields, isSubmitting, errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: inviteFor || '',
      password: ''
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(getEnvironmentName() !== 'production');
  const [isAuthenticatingDefault, setIsAuthenticatingDefault] = useState(false);
  const navigate = useNavigate();
  const authenticate = useUserStore((state) => state.authenticate);
  const redirectUrl = location?.state?.callbackUrl || '/';
  const { openCredsErrorToast, openErrorToast, openOTPSuccessToast, openOTPErrorToast } =
    useLoginShared();

  const handleNonOAuthSignIn = async (data) => {
    if (showPasswordField) {
      return handleDefaultSignIn(data);
    } else {
      return handleOTPSignIn(data);
    }
  };

  const handleDefaultSignIn = async ({ email, password }) => {
    if (!email || !password) return;
    setIsAuthenticatingDefault(true);
    try {
      await authenticate(email, password);
      setIsAuthenticatingDefault(false);
      navigate(redirectUrl);
    } catch (e) {
      if (e?.status !== 422) {
        Sentry.withScope((scope) => {
          scope.setTag('error_type', 'login_error');
          scope.setExtra('error_message', 'Non-credentials error during login');
          Sentry.captureException(e);
        });
      }
      openCredsErrorToast(e);
      setIsAuthenticatingDefault(false);
    }
  };

  const handleOTPSignIn = async ({ email }) => {
    if (!email) return;
    setIsAuthenticatingDefault(true);
    try {
      await generateOtp(email, paymentId);
      openOTPSuccessToast();
      setIsAuthenticatingDefault(false);
    } catch (e) {
      openOTPErrorToast(
        e?.response?.data?.errors?.join(', ') || 'An unexpected error occurred. Please try again.'
      );
      setIsAuthenticatingDefault(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const values = getValues();
      return handleSubmit(handleNonOAuthSignIn(values));
    }
  };

  return (
    <Flex
      data-cy='login-page'
      h='full'
      alignItems='flex-start'
      bgColor='surface.containerLow'
      flexDir='column'
    >
      <Center
        h='full'
        w='full'
        px={{ base: 8, lg: 0 }}
      >
        <VStack
          w='sm'
          bgColor='surface.containerLowest'
          border='reskin'
          borderRadius='xl'
        >
          <Flex
            h='60px'
            w='full'
            bgColor='brandPrimary.primary'
            align='center'
            justify='center'
            borderTopRadius='xl'
          >
            <Image
              src={ForaLogoWhite}
              alt='logo'
              maxBlockSize={4}
              onClick={() => navigate('/feed')}
              cursor='pointer'
              zIndex={20}
            />
          </Flex>
          {isSubmitting ? (
            <Spinner
              size='lg'
              thickness='3px'
              speed='0.75s'
              mt={12}
              mb={24}
            />
          ) : (
            <>
              <Flex
                as='form'
                flexDir='column'
                width='full'
                gap={8}
                onSubmit={handleSubmit(handleNonOAuthSignIn)}
                py={1.5}
                px={4}
              >
                {inviteFor && (
                  <Alert size='md'>
                    Welcome! To get started, please log in via an identity provider or login link.
                  </Alert>
                )}

                <OAuthStack
                  redirectUrl={redirectUrl}
                  isSubmitting={isSubmitting}
                  openCredsErrorToast={openCredsErrorToast}
                  openErrorToast={openErrorToast}
                  oktaEnabled={oktaEnabled}
                />

                {standardLoginEnabled && (
                  <>
                    <Text
                      color='surface.onSurfaceVariant'
                      align='center'
                      fontWeight={400}
                    >
                      Or continue with
                    </Text>
                    <VStack gap={4}>
                      <FormInput
                        key='email'
                        type='email'
                        label='Email Address'
                        errorMsg={errors.email?.message}
                        registerProps={{ ...register('email', { required: true }) }}
                        size='lg'
                        onKeyDown={handleKeyDown}
                        bgColor='surface.surface'
                        _hover={{ bgColor: 'surface.surfaceDim' }}
                        _focus={{ bgColor: 'surface.surfaceDim' }}
                      />
                      {!showPasswordField && (
                        <>
                          <Button
                            type='submit'
                            variant='reskinPrimary'
                            w='full'
                            h='48px'
                            isDisabled={isAuthenticatingDefault || isSubmitting}
                            color='brandPrimary.onPrimary'
                          >
                            Send me a Login Link
                            {(isAuthenticatingDefault || isSubmitting) && (
                              <Spinner
                                size='xs'
                                position='absolute'
                                top={0.5}
                                right={-6}
                              />
                            )}
                          </Button>
                          {passwordEnabled && (
                            <Box>
                              <Text
                                variant='body-sm'
                                cursor='pointer'
                                onClick={() => {
                                  setShowPasswordField(true);
                                }}
                              >
                                Or log in with a password
                              </Text>
                            </Box>
                          )}
                        </>
                      )}
                      {showPasswordField && (
                        <>
                          <Box
                            position='relative'
                            w='full'
                          >
                            <FormInput
                              key='password'
                              name='password'
                              type={showPassword ? 'text' : 'password'}
                              label='Password'
                              errorMsg={errors.password?.message}
                              registerProps={{ ...register('password', { required: true }) }}
                              size='lg'
                              onKeyDown={handleKeyDown}
                              bgColor='surface.surface'
                              _hover={{ bgColor: 'surface.surfaceDim' }}
                              _focus={{ bgColor: 'surface.surfaceDim' }}
                            />
                            {dirtyFields?.password && (
                              <IconButton
                                aria-label='Show PW'
                                variant='ghost'
                                size='sm'
                                position='absolute'
                                right={4}
                                top={3}
                                icon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                onClick={() => setShowPassword(!showPassword)}
                              />
                            )}
                          </Box>
                          <Button
                            type='submit'
                            variant='reskinPrimary'
                            w='full'
                            h='48px'
                            isDisabled={isAuthenticatingDefault || isSubmitting}
                            color='brandPrimary.onPrimary'
                          >
                            <Box position='relative'>
                              Sign in with Password
                              {(isAuthenticatingDefault || isSubmitting) && (
                                <Spinner
                                  size='xs'
                                  position='absolute'
                                  top={0.5}
                                  right={-6}
                                />
                              )}
                            </Box>
                          </Button>
                          <Box>
                            <Text
                              variant='body-sm'
                              cursor='pointer'
                              onClick={() => {
                                setShowPasswordField(false);
                              }}
                            >
                              Or use a login link
                            </Text>
                          </Box>
                        </>
                      )}
                    </VStack>
                  </>
                )}
              </Flex>
              <TermsAndConditions />
            </>
          )}
        </VStack>
      </Center>
    </Flex>
  );
}

export default LoginPage;
